/**
 * Floating overlays for the Report Builder docx editor, rendered in a portal
 * and anchored to the pointer (not DOM coordinates — see below):
 *
 *   • Selection bubble — appears on a non-empty selection:
 *       – Refine Wording   — always offered on a selection
 *       – Original Wording — SHOWN only when the selection matches a snapshot
 *                            captured by a previous Refine click (i.e. there is
 *                            something to revert); hidden otherwise
 *   • Dictate mic      — appears at the caret (collapsed selection) so
 *                        dictated text has somewhere to land
 *
 * We deliberately don't position bubbles or carets via DOM coordinates,
 * because docx-editor's paginated rendering places page DOMs outside the
 * ProseMirror `view.dom`, which makes `coordsAtPos` and the
 * `view.dom.contains(...)` checks unreliable for popovers. Anchoring the
 * actions to a stable spot avoids that whole class of bug.
 *
 * Edits go through plain ProseMirror transactions. To find PM positions
 * without trusting `view.state.selection` (which docx-editor doesn't keep
 * in sync), we read `editorRef.current.getSelectionInfo()` for
 * `{ paraId, before, selectedText }`, then walk the PM doc to find the
 * paragraph node with that `paraId` and compute the position by adding
 * `before.length` characters from the paragraph's start.
 */
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import type { DocxEditorRef } from "@eigenpal/docx-editor-react";
import { Mic, RotateCcw, Sparkles, Undo2 } from "lucide-react";
import { Fragment, type Node as PMNode, Slice } from "prosemirror-model";
import { type EditorState, TextSelection } from "prosemirror-state";
import { createPortal } from "react-dom";

import { ConfirmationModal } from "@components/ConfirmationModal";
import { Transcriber } from "@components/Transcriber";
import type { TranscriptionCallback } from "@components/Transcriber/hooks";
import { Button } from "@components/ui/button";

import { useRefineWording } from "@pages/ReportStudio/ReportBuilder/hooks/use-refine-wording";

import {
  type Refinement,
  findRefinementToRevert,
  locateUniqueOccurrence,
  recordRefinement,
  stripWs,
} from "../utils/refinement-snapshots";
import { caretPmPosWithinParagraph } from "./caret-position";
import { buildInlineNodes, visualParaAttrs } from "./para-clone";
import {
  EMPHASIS_MARK_NAMES,
  applyEmphasisPhrases,
  collectEmphasisPhrases,
} from "./refine-emphasis";
import {
  captureRefineAnchor,
  rebaseRefineRange,
  refineRangeMessage,
} from "./refine-range";
import {
  type OriginalBlock,
  buildStructuredRefine,
  checkRefineSelection,
  refineSelectionMessage,
  splitRefinedParagraphs,
} from "./refine-selection";
import { donorMarksAt, findParagraphStart } from "./slots";

type SelectionInfo = {
  paraId: string | null;
  selectedText: string;
  paragraphText: string;
  before: string;
  after: string;
};

type OverlayState = {
  hasSelection: boolean;
  /** Whether the current selection has a recorded refinement to revert. Drives
   *  showing/hiding the "Original Wording" action — derived in `recompute` (not
   *  render) so it stays reactive without reading a ref during render. */
  canRestore: boolean;
  hasCaret: boolean;
  /** Viewport-space point where the user finished their drag-selection.
   *  We position the bubble relative to this, since docx-editor renders
   *  its own selection highlight (no `window.getSelection()` rect) and
   *  PM's `coordsAtPos` returns coords for the hidden source-of-truth
   *  DOM at `left:-9999`. */
  anchor: { x: number; y: number } | null;
  /** Visible-caret position — the anchor for the floating caret mic.
   *  Derived from `document.caretRangeFromPoint(x, y)` on the click,
   *  which is the same browser primitive used to place the caret, so
   *  the mic and caret render in lockstep. For multi-line / non-empty
   *  paragraphs the caret can sit far below the paragraph's outer top,
   *  so we read the caret rect directly rather than the paragraph rect
   *  (MDRS-91). Falls back to the paragraph rect when the caret API is
   *  unavailable (older browsers) or returns an empty rect. */
  caretAnchor: { x: number; y: number } | null;
};

const INITIAL_STATE: OverlayState = {
  hasSelection: false,
  canRestore: false,
  hasCaret: false,
  anchor: null,
  caretAnchor: null,
};

/**
 * MDRS-292: has the in-flight interim (partial-dictation) range at
 * `[from, from+size]` been left untouched since we wrote it? Guards the
 * partial-streaming replace path — if the user typed into the range (or a
 * position drifted), the doc text no longer matches the last partial and we
 * abandon the range rather than replace the wrong span. `"\n"` renders a
 * hardBreak leaf so multi-line partials compare correctly.
 */
const partialRangeIntact = (
  doc: PMNode,
  from: number,
  size: number,
  expected: string
): boolean => {
  if (from < 0 || from + size > doc.content.size) return false;
  try {
    return doc.textBetween(from, from + size, "\n", "\n") === expected;
  } catch {
    return false;
  }
};

/**
 * Resolve the exact PM range `[from, to]` for the reported selection.
 *
 * `getSelectionInfo()` describes only the selection's ANCHOR paragraph (a single
 * `paraId` plus `before`/`selectedText`), so the old `to = from + selectedText
 * .length` math undercounts across paragraph boundaries — PM positions include
 * the block open/close tokens (and empty spacer paragraphs) that the flat string
 * doesn't — leaving the original's tail behind on a multi-paragraph refine (the
 * stray `nts.`). The fork's `getSelectionInfo` itself derives from the PM view,
 * so `state.selection` IS the accurate range: trust it when its text matches
 * `selectedText` (guards a lagging/stale selection), else fall back to the
 * single-paragraph string math, which is exact when no boundary is crossed.
 *
 * Exported for unit testing.
 */
export const resolvePmRange = (
  state: EditorState,
  info: { before: string; selectedText: string },
  paraPos: number
): { from: number; to: number } => {
  const stringFrom = paraPos + 1 + info.before.length;
  const fallback = {
    from: stringFrom,
    to: stringFrom + info.selectedText.length,
  };
  const sel = state.selection;
  if (!sel || sel.empty) return fallback;
  try {
    const selText = state.doc.textBetween(sel.from, sel.to, "\n", "\n");
    // Whitespace-INSENSITIVE compare: `textBetween` inserts a break at each
    // paragraph boundary while `getSelectionInfo().selectedText` concatenates
    // paragraphs with no separator, so a space-preserving compare would never
    // match a multi-paragraph selection and we'd wrongly fall back.
    if (stripWs(selText) === stripWs(info.selectedText)) {
      return { from: sel.from, to: sel.to };
    }
  } catch {
    /* fall through to the string-math fallback */
  }
  return fallback;
};

type EditorOverlaysProps = {
  editorRef: RefObject<DocxEditorRef | null>;
  /**
   * Visible-DOM scope for caret/selection overlays. A click is only treated
   * as targeting this editor when its `target` is inside `scopeRef.current`.
   * docx-editor renders PM off-screen at `left:-9999`, so walking
   * `getView().dom` ancestors to find `.ep-root` is unreliable — pass the
   * wrapping container ref instead.
   */
  scopeRef: RefObject<HTMLElement | null>;
};

export const EditorOverlays = ({ editorRef, scopeRef }: EditorOverlaysProps) => {
  const [state, setState] = useState<OverlayState>(INITIAL_STATE);
  // MDRS-292 (Nat): moving the cursor while caret dictation is live prompts to
  // stop, mirroring Assessment Scribe. `caretPos` is where the caret should
  // land if the user confirms "Stop"; null when the click didn't resolve to a
  // caret. Non-null value = modal open.
  const [stopPrompt, setStopPrompt] = useState<{ caretPos: number | null } | null>(
    null
  );

  // Refine-wording undo snapshots, tracked per paragraph and matched on
  // normalised text (MDRS-165). Session-only — we can't store these in
  // docx-editor's OOXML schema without a fork. See utils/refinement-snapshots.
  const refinementsRef = useRef<Refinement[]>([]);

  const { refine, isRefining, showError, showSuccess } = useRefineWording();

  const readInfo = useCallback((): SelectionInfo | null => {
    const info = editorRef.current?.getSelectionInfo?.();
    if (!info) return null;
    return info;
  }, [editorRef]);

  // Most recent mouseup point inside the editor. Captured below by a
  // document-level listener so we have a viewport coord to anchor the
  // bubble to whenever the editor reports a new selection.
  const anchorRef = useRef<{ x: number; y: number } | null>(null);
  // Anchor for the floating caret mic — paragraph rect top-left, set
  // on click. See OverlayState.caretAnchor for the why (MDRS-91).
  const caretAnchorRef = useRef<{ x: number; y: number } | null>(null);
  // Fallback paragraph identifier + PM positions derived from the DOM
  // click target. docx-editor renders each paragraph as a
  // `.layout-paragraph` div tagged with `data-pm-start` / `data-pm-end`
  // — those are PM doc positions we can dispatch transactions to.
  const clickedParaIdRef = useRef<string | null>(null);
  const clickedPmRangeRef = useRef<{ from: number; to: number } | null>(null);
  // MDRS-292: the exact PM position of the caret under the click (mapped from the
  // painted paragraph via caret-position.ts), so dictation inserts where the user
  // clicked instead of at the end of the paragraph. Null when unmappable — the
  // insert then falls back to the old end-of-paragraph behaviour.
  const clickedCaretPosRef = useRef<number | null>(null);
  // Whether the most recent mouseup landed inside this editor. Used as a
  // multi-editor scope gate (e.g. Preview modal mounts a second docx
  // instance). We can't rely on `document.activeElement` because docx-editor
  // doesn't render a contenteditable — focus stays on body / a tabIndex=0
  // ancestor that often isn't inside `.ep-root`.
  const isActiveEditorRef = useRef(false);
  // docx-editor's `onSelectionChange` stream is the authoritative signal for
  // whether a range selection is active. `getSelectionInfo().selectedText`
  // can lag (or stay empty for paginated range selections), so we cache the
  // latest reported state and prefer its `hasSelection` flag.
  const editorSelectionRef = useRef<{ hasSelection: boolean } | null>(null);
  // True while caret-anchored dictation is actively streaming. Set in
  // `caretDictationCallback.onFocus` (called by Transcriber when the WS +
  // mic come online) and cleared in `onEnd`. Used to gate `hide()` — we
  // must NOT unmount the mic portal mid-dictation, because that tears
  // down the Transcriber's WebSocket and AudioContext, killing the
  // session. The user wants to scroll/click around while still speaking.
  const isCaretDictationActiveRef = useRef(false);

  const recompute = useCallback(() => {
    if (!isActiveEditorRef.current) {
      setState(INITIAL_STATE);
      return;
    }

    const info = readInfo();
    const selectedTextLen = info?.selectedText.length ?? 0;
    // The browser tracks the native selection over the rendered `.layout-
    // paragraph` divs even though docx-editor doesn't always reflect it in
    // `getSelectionInfo`. This is the most reliable signal — `document
    // .selectionchange` updates it live during the user's drag.
    const winSel = typeof window !== "undefined" ? window.getSelection() : null;
    const nativeHasSelection =
      !!winSel && !winSel.isCollapsed && winSel.toString().length > 0;
    const hasSelection =
      nativeHasSelection ||
      !!editorSelectionRef.current?.hasSelection ||
      selectedTextLen > 0;
    // hasCaret: docx-editor surfaces a paraId, OR the user clicked on a
    // `.layout-paragraph` and we captured its PM range from the DOM.
    const hasCaret =
      !hasSelection && (!!info?.paraId || clickedPmRangeRef.current !== null);
    // Only offer "Original Wording" (revert) when the current selection actually
    // has a recorded refinement to undo — otherwise clicking it just errors.
    // Reuses the same matcher handleOriginal uses, so "shown" and "actionable"
    // can't drift apart. Computed here (an event callback) rather than in render
    // so reading refinementsRef is fine and canRestore stays reactive.
    const canRestore =
      hasSelection && !!info?.paraId
        ? !!findRefinementToRevert(refinementsRef.current, {
            paraId: info.paraId,
            selectedText: info.selectedText,
            paragraphText: info.paragraphText,
          })
        : false;
    setState({
      hasSelection,
      canRestore,
      hasCaret,
      anchor: anchorRef.current,
      caretAnchor: caretAnchorRef.current,
    });
  }, [readInfo, editorRef]);

  // Subscribe to docx-editor's selection-change stream. DOM selectionchange
  // is a belt-and-braces backup in case the editor doesn't notify for some
  // transition (e.g. collapsed-caret moves between paragraphs).
  useEffect(() => {
    const handle = () => recompute();
    // Hide all overlays — used by scroll/resize so the bubble/mic don't sit
    // over the wrong content (anchor is viewport-fixed; scrolling moves the
    // content underneath it).
    //
    // Exception: when caret dictation is in progress, leave everything
    // in place. Clearing `caretAnchorRef` here would unmount the mic
    // portal, which destroys the live Transcriber's WS + audio graph
    // and silently drops the dictation. Users expect to be able to
    // scroll the document while still speaking. The mic stays at its
    // last fixed-viewport position; once they stop dictating, the next
    // recompute or hide() will clear it normally.
    const hide = () => {
      if (isCaretDictationActiveRef.current) return;
      isActiveEditorRef.current = false;
      anchorRef.current = null;
      caretAnchorRef.current = null;
      clickedParaIdRef.current = null;
      clickedPmRangeRef.current = null;
      clickedCaretPosRef.current = null;
      setState(INITIAL_STATE);
    };
    // Track where mousedown landed so mouseup can tell a click from a drag.
    // A drag means the user is selecting text — we must NOT capture a caret
    // range for the mic, otherwise the mic shows alongside the selection
    // bubble (or before docx-editor reports the selection through info).
    const mouseDownPosRef = { x: 0, y: 0, valid: false };
    const onMouseDown = (e: MouseEvent) => {
      mouseDownPosRef.x = e.clientX;
      mouseDownPosRef.y = e.clientY;
      mouseDownPosRef.valid = true;
    };
    const onMouseUp = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const downX = mouseDownPosRef.x;
      const downY = mouseDownPosRef.y;
      const hadDown = mouseDownPosRef.valid;
      mouseDownPosRef.valid = false;
      const dragDistance = hadDown
        ? Math.hypot(e.clientX - downX, e.clientY - downY)
        : 0;
      // 4px is the standard click-vs-drag threshold — below it, treat as a
      // click; above, treat as a selection drag.
      const isDrag = dragDistance > 4;

      // Skip clicks on our own floating UI — pressing the mic must not
      // mutate the captured paragraph (would unmount Transcriber mid-init).
      if (target?.closest?.("[data-overlay-portal]")) return;

      // Scope to THIS editor only (MDRS-26). docx-editor renders PM
      // off-screen at `left:-9999` separate from the visible `.ep-root`
      // tree, so `getView().dom.closest('.ep-root')` resolves to null
      // and the mic anchors to any click on the page. The wrapping div
      // ref passed in by the parent is the reliable visible scope.
      // Fail closed: if the scope ref isn't attached yet (e.g. mid-mount)
      // we drop the mic rather than let it anchor anywhere on the page.
      const scope = scopeRef.current;
      if (!scope || (target && !scope.contains(target))) {
        // Outside-editor click during active caret dictation: leave the
        // session alone (same reasoning as in `hide()` above — clearing
        // the anchor unmounts the live Transcriber and stops dictation).
        if (isCaretDictationActiveRef.current) return;
        isActiveEditorRef.current = false;
        clickedParaIdRef.current = null;
        clickedPmRangeRef.current = null;
        clickedCaretPosRef.current = null;
        caretAnchorRef.current = null;
        setTimeout(handle, 0);
        return;
      }

      isActiveEditorRef.current = true;
      anchorRef.current = { x: e.clientX, y: e.clientY };

      // Drag → user is selecting. Don't capture a caret range — the mic must
      // not appear under the selection. The bubble will show once docx-editor
      // surfaces the selection through `info.selectedText`.
      if (isDrag) {
        clickedParaIdRef.current = null;
        clickedPmRangeRef.current = null;
        clickedCaretPosRef.current = null;
        caretAnchorRef.current = null;
        setTimeout(handle, 0);
        setTimeout(handle, 80);
        setTimeout(handle, 250);
        return;
      }

      // Find the paragraph the click is *aimed at*. We can't trust
      // `target.closest('.layout-paragraph')` because clicks in the
      // gap between paragraphs land on a container element, not on
      // the paragraph the user is reading. Use the browser's
      // caret-at-point API to snap to the nearest text caret, then
      // walk up to its containing `.layout-paragraph`.
      const findParagraphAt = (x: number, y: number): HTMLElement | null => {
        const direct = document.elementFromPoint(x, y) as HTMLElement | null;
        const closeHit = direct?.closest?.(
          ".layout-paragraph"
        ) as HTMLElement | null;
        if (closeHit) return closeHit;
        let caretNode: Node | null = null;
        if (typeof document.caretRangeFromPoint === "function") {
          caretNode = document.caretRangeFromPoint(x, y)?.startContainer ?? null;
        } else if (
          typeof (
            document as Document & {
              caretPositionFromPoint?: (
                x: number,
                y: number
              ) => { offsetNode: Node } | null;
            }
          ).caretPositionFromPoint === "function"
        ) {
          const fn = (
            document as Document & {
              caretPositionFromPoint: (
                x: number,
                y: number
              ) => { offsetNode: Node } | null;
            }
          ).caretPositionFromPoint;
          caretNode = fn(x, y)?.offsetNode ?? null;
        }
        if (!caretNode) return null;
        const el =
          caretNode.nodeType === 1
            ? (caretNode as Element)
            : caretNode.parentElement;
        return (el?.closest(".layout-paragraph") as HTMLElement | null) ?? null;
      };

      const paraEl = findParagraphAt(e.clientX, e.clientY);

      // Read the visible caret's viewport rect at the click point, using
      // the same browser primitive that places the caret. For multi-line
      // or non-empty paragraphs the caret can sit far below the para's
      // outer top, so anchoring to paraEl.getBoundingClientRect() pinned
      // the mic to line 1 while the caret was on line N (MDRS-91 follow-
      // up). Firefox uses caretPositionFromPoint; everyone else exposes
      // caretRangeFromPoint. Returns null when neither API is available
      // or the result is an empty rect — caller falls back to the
      // paragraph rect.
      // MDRS-292: return the caret rect AND its DOM (node, offset). The rect
      // anchors the mic (MDRS-91); the (node, offset) maps to a PM position so
      // dictation inserts at the caret rather than the end of the paragraph.
      const readCaretPoint = (
        x: number,
        y: number
      ): { rect: DOMRect; node: Node | null; offset: number } | null => {
        let rect: DOMRect | null = null;
        let node: Node | null = null;
        let offset = 0;
        if (typeof document.caretRangeFromPoint === "function") {
          const range = document.caretRangeFromPoint(x, y);
          if (range) {
            rect = range.getBoundingClientRect();
            node = range.startContainer;
            offset = range.startOffset;
          }
        } else if (
          typeof (
            document as Document & {
              caretPositionFromPoint?: (
                x: number,
                y: number
              ) => { offsetNode: Node; offset: number } | null;
            }
          ).caretPositionFromPoint === "function"
        ) {
          const fn = (
            document as Document & {
              caretPositionFromPoint: (
                x: number,
                y: number
              ) => { offsetNode: Node; offset: number } | null;
            }
          ).caretPositionFromPoint;
          const pos = fn(x, y);
          if (pos) {
            const range = document.createRange();
            range.setStart(pos.offsetNode, pos.offset);
            range.collapse(true);
            rect = range.getBoundingClientRect();
            node = pos.offsetNode;
            offset = pos.offset;
          }
        }
        // Return null only when there's no usable rect — the rect still anchors
        // the mic even if `node` couldn't be read (the PM mapping below simply
        // falls back). Some browsers return 0/0/0/0 for collapsed ranges in
        // empty text nodes — treat that as unusable.
        if (!rect) return null;
        if (
          rect.top === 0 &&
          rect.left === 0 &&
          rect.width === 0 &&
          rect.height === 0
        ) {
          return null;
        }
        return { rect, node, offset };
      };

      // MDRS-292 (Nat): the cursor is being moved while a caret-dictation
      // session is live. Don't silently re-anchor the mic or redirect the
      // stream — prompt the user to stop first (mirrors Assessment Scribe).
      // Resolve the target caret so "Stop" can place the cursor where they
      // clicked; leave the mic + live session untouched until they decide.
      if (isCaretDictationActiveRef.current) {
        if (!paraEl) return; // off editable text → keep the session, ignore
        const pmStartAttr = paraEl.getAttribute("data-pm-start");
        const caret = pmStartAttr ? readCaretPoint(e.clientX, e.clientY) : null;
        const targetPos = caret?.node
          ? caretPmPosWithinParagraph(paraEl, caret.node, caret.offset)
          : null;
        setStopPrompt({ caretPos: targetPos });
        return;
      }

      if (paraEl) {
        const pmStart = paraEl.getAttribute("data-pm-start");
        const pmEnd = paraEl.getAttribute("data-pm-end");
        if (pmStart && pmEnd) {
          clickedParaIdRef.current = paraEl.getAttribute("data-block-id") ?? null;
          clickedPmRangeRef.current = {
            from: Number(pmStart),
            to: Number(pmEnd),
          };
          // Mic follows the visible caret. Fall back to the paragraph
          // rect when the caret API is unavailable so the mic still
          // shows in a sensible spot.
          const caret = readCaretPoint(e.clientX, e.clientY);
          const anchorRect = caret?.rect ?? paraEl.getBoundingClientRect();
          caretAnchorRef.current = { x: anchorRect.left, y: anchorRect.top };
          // MDRS-292: remember the exact PM caret position under the click so
          // dictation inserts there (not at the end of the paragraph). Null when
          // the caret API is unavailable / the click wasn't on text.
          clickedCaretPosRef.current = caret?.node
            ? caretPmPosWithinParagraph(paraEl, caret.node, caret.offset)
            : null;
        } else {
          caretAnchorRef.current = null;
          clickedCaretPosRef.current = null;
        }
      } else {
        // No paragraph under the click. Two cases land here:
        //   1. Click outside the editor entirely (handled by scope check
        //      above, so this branch in practice only sees gutters /
        //      between-pages whitespace / page chrome inside `.ep-root`).
        //   2. Click in a non-editable region of the editor (gutter,
        //      page-break decoration, margin).
        // Either way there's no editable line for dictation to insert
        // into, so drop the mic (MDRS-26). Clicks on images / table
        // cells inside a paragraph still resolve to the paragraph via
        // `target.closest('.layout-paragraph')` in findParagraphAt and
        // don't hit this branch.
        clickedParaIdRef.current = null;
        clickedPmRangeRef.current = null;
        clickedCaretPosRef.current = null;
        caretAnchorRef.current = null;
        isActiveEditorRef.current = false;
      }

      setTimeout(handle, 0);
      setTimeout(handle, 80);
      setTimeout(handle, 250);
    };
    const editor = editorRef.current;
    const unsub = editor?.onSelectionChange?.((state) => {
      editorSelectionRef.current = state
        ? { hasSelection: !!state.hasSelection }
        : null;
      handle();
    });
    document.addEventListener("selectionchange", handle);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mouseup", onMouseUp);
    // Scroll/resize: hide overlays rather than re-position them. The anchor
    // is in viewport coords, so the bubble/mic would otherwise stick to the
    // screen while the content scrolls past — pointing at the wrong text.
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    handle();
    return () => {
      unsub?.();
      document.removeEventListener("selectionchange", handle);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [editorRef, scopeRef, recompute]);

  const getView = useCallback(
    () => editorRef.current?.getEditorRef()?.getView() ?? null,
    [editorRef]
  );

  // ── Refine wording ────────────────────────────────────────────────────
  const handleRefine = useCallback(async () => {
    const info = readInfo();
    if (!info?.paraId || !info.selectedText.trim()) return;
    const view = getView();
    if (!view) return;

    const paraPos = findParagraphStart(view, info.paraId);
    if (paraPos === null) {
      showError("Couldn't locate the selection in the document.");
      return;
    }

    const { from, to } = resolvePmRange(view.state, info, paraPos);

    // S1-RW-06: the refine service returns plain prose, so re-inserting it
    // flattens anything structural. Refuse bulk selections (tables/lists, or
    // mixed headings/paragraphs) with a clear message and NO changes, rather
    // than silently damaging the document's formatting.
    const refineCheck = checkRefineSelection(view.state.doc, from, to);
    if (!refineCheck.ok) {
      showError(refineSelectionMessage(refineCheck.reason));
      return;
    }

    // Send the selection as plain text WITH its paragraph breaks preserved
    // (block boundary → blank line, hardBreak → newline). The fork's
    // getSelectionInfo concatenates paragraphs with no separator, so we read the
    // text straight from the PM range instead of `info.selectedText`.
    const sourceText =
      view.state.doc.textBetween(from, to, "\n\n", "\n").trim() || info.selectedText;

    // S1-RW-06: capture the emphasised spans BEFORE refining so we can re-apply
    // bold/italic/underline to the phrases that survive the reword (the service
    // strips any markup we send, so this is done client-side on the way back).
    const emphasisPhrases = collectEmphasisPhrases(view.state.doc, from, to);

    // MDRS-549: snapshot the EXACT original slice (not a flat string) so revert
    // restores paragraph structure verbatim — a flat-string revert rebuilt as
    // inline content merged paragraphs on a multi-paragraph selection. Taken
    // BEFORE the await, alongside `emphasisPhrases`: captured afterwards it read
    // the stale offsets, so revert restored whatever a concurrent edit had
    // shifted into them rather than the text the user selected.
    const originalSlice = view.state.doc.slice(from, to);

    // MDRS-549: remember HOW to find this range again. PM positions are absolute
    // document offsets and the editor stays editable during the call (dictation
    // must not be cut off mid-thought), so anything dispatched while the request
    // is in flight shifts them. See refine-range.ts.
    const anchor = captureRefineAnchor(view.state, {
      paraId: info.paraId,
      paraPos,
      from,
      to,
      sourceText,
    });

    const refined = await refine(sourceText);
    if (!refined) return;

    // MDRS-549: re-find the range in the document as it stands NOW, and refuse
    // to write anything if the text there is no longer what we refined.
    const rebased = rebaseRefineRange(view.state, anchor);
    if (!rebased.ok) {
      showError(refineRangeMessage(rebased.reason));
      return;
    }
    const { from: targetFrom, to: targetTo } = rebased;

    // Re-run the structural guard on the rebased range: the text matches, but a
    // concurrent edit could have changed the blocks around it.
    const rebasedCheck = checkRefineSelection(view.state.doc, targetFrom, targetTo);
    if (!rebasedCheck.ok) {
      showError(refineSelectionMessage(rebasedCheck.reason));
      return;
    }

    refinementsRef.current = recordRefinement(refinementsRef.current, {
      paraId: info.paraId,
      original: sourceText,
      refined,
      originalSliceJSON: originalSlice.toJSON(),
    });

    // MDRS-264: inherit the marks already on the text being replaced so the
    // refined wording keeps the template's font/size instead of falling back
    // to the schema default — same treatment pastePlugin/dropPlugin apply.
    // S1-RW-06: strip EMPHASIS marks (bold/italic/underline) here — they're
    // re-applied per-phrase by applyEmphasisPhrases, not smeared across the
    // whole selection from the caret's marks. Font/size/colour are kept.
    // MDRS-549: read the marks from the REBASED range — at the stale offsets
    // these came from whatever text had shifted into them, so the refined
    // wording inherited the wrong font.
    const marks = donorMarksAt(view.state, targetFrom, true).filter(
      (m) => !EMPHASIS_MARK_NAMES.has(m.type.name)
    );
    // Turn a refined paragraph's text into inline nodes: font marks on every
    // run + emphasis re-applied to phrases that survived the reword (S1-RW-06).
    const buildInline = (text: string) =>
      applyEmphasisPhrases(view.state.schema, text, marks, emphasisPhrases);
    const $from = view.state.doc.resolve(targetFrom);
    const $to = view.state.doc.resolve(targetTo);
    // A cross-paragraph selection must be rebuilt as paragraph nodes; replacing
    // it with inline content (buildInlineNodes) collapses the paragraphs into
    // one. Single-paragraph selections keep the inline path unchanged.
    const spansParagraphs = !$from.sameParent($to);
    try {
      let tr;
      if (spansParagraphs) {
        // S1-RW-06: preserve the ORIGINAL paragraph structure — each content
        // paragraph keeps its own visual attrs and blank separator paragraphs
        // are re-emitted — so a multi-paragraph refine doesn't collapse the
        // paragraphs together (which flattened their spacing before).
        const originalBlocks: OriginalBlock[] = [];
        view.state.doc.nodesBetween(targetFrom, targetTo, (node) => {
          if (node.isTextblock) {
            originalBlocks.push({
              attrs: visualParaAttrs(node),
              isEmpty: node.textContent.trim().length === 0,
            });
            return false;
          }
          return true;
        });
        const rebuilt = buildStructuredRefine(
          view.state.schema,
          originalBlocks,
          splitRefinedParagraphs(refined),
          buildInline
        );
        if (rebuilt === null) {
          // The refine merged/split paragraphs (or the schema has no paragraph
          // node), so it can't be mapped back 1:1 without damaging structure —
          // leave the document unchanged and tell the user.
          showError(
            "Refine changed the paragraph structure, so your text was left unchanged. Try refining one paragraph at a time."
          );
          return;
        }
        // Open both ends so the first/last rebuilt paragraphs merge into the
        // prefix/suffix left in the boundary paragraphs at the selection edges.
        const slice = new Slice(Fragment.fromArray(rebuilt), 1, 1);
        tr = view.state.tr.replace(targetFrom, targetTo, slice);
      } else {
        // Single paragraph: re-apply surviving inline emphasis + font marks.
        const fragment = Fragment.from(buildInline(refined));
        tr = view.state.tr.replaceWith(targetFrom, targetTo, fragment);
      }
      view.dispatch(tr);
      showSuccess("Wording refined.");
    } catch (err) {
      console.error("[overlay] refine replace failed:", err);
      showError("Failed to apply refined text.");
    }
  }, [getView, readInfo, refine, showError, showSuccess]);

  // ── Original wording ──────────────────────────────────────────────────
  const handleOriginal = useCallback(() => {
    const info = readInfo();
    if (!info?.paraId) return;

    // Match on normalised text (and, if the selection lags empty, on the
    // paragraph body) so revert isn't defeated by whitespace differences or a
    // lagging first-click selection (MDRS-165).
    const match = findRefinementToRevert(refinementsRef.current, {
      paraId: info.paraId,
      selectedText: info.selectedText,
      paragraphText: info.paragraphText,
    });
    if (!match) {
      showError(
        "No matching refined text. Refine the selection first to enable undo."
      );
      return;
    }
    const view = getView();
    if (!view) return;
    const paraPos = findParagraphStart(view, info.paraId);
    if (paraPos === null) {
      showError("Couldn't locate the selection in the document.");
      return;
    }

    // Prefer the live selection's range when it exactly covers the refined text
    // (resolvePmRange trusts the real PM selection there, so a multi-paragraph
    // refined range is handled correctly); otherwise (partial or lagging/empty
    // selection) locate the refined text inside the anchor paragraph — the
    // single-paragraph fallback, which maps offsets as `paraPos + 1 + offset`.
    let from: number;
    let to: number;
    const selNorm = stripWs(info.selectedText);
    if (selNorm && selNorm === stripWs(match.refined)) {
      ({ from, to } = resolvePmRange(view.state, info, paraPos));
    } else {
      // Ambiguous (refined text absent or appearing more than once) → bail
      // rather than revert an occurrence the user didn't refine.
      const idx = locateUniqueOccurrence(info.paragraphText, match.refined);
      if (idx < 0) {
        showError("Couldn't locate the refined text to revert.");
        return;
      }
      from = paraPos + 1 + idx;
      to = from + match.refined.length;
    }

    try {
      let tr;
      if (match.originalSliceJSON) {
        // Restore the exact original slice — preserves paragraph boundaries that
        // an inline rebuild would collapse (the revert paragraph-merge bug).
        const slice = Slice.fromJSON(view.state.schema, match.originalSliceJSON);
        tr = view.state.tr.replace(from, to, slice);
      } else {
        // Legacy snapshot (pre-slice, single-paragraph) → inline rebuild.
        // MDRS-264: inherit the marks on the refined text being reverted so the
        // restored wording keeps the template's font/size.
        const marks = donorMarksAt(view.state, from, true);
        const fragment = Fragment.from(
          buildInlineNodes(view.state.schema, match.original, marks)
        );
        tr = view.state.tr.replaceWith(from, to, fragment);
      }
      view.dispatch(tr);
      refinementsRef.current = refinementsRef.current.filter((r) => r !== match);
      showSuccess("Original wording restored.");
    } catch (err) {
      console.error("[overlay] restore failed:", err);
      showError("Failed to restore original wording.");
    }
  }, [getView, readInfo, showError, showSuccess]);

  // canRestore (whether the selection has a refinement to revert) is derived in
  // recompute alongside hasSelection — see there. We HIDE "Original Wording"
  // when false rather than disable it, so the bubble isn't cluttered with a dead
  // button in the common case where nothing has been refined.
  const { hasSelection, canRestore, hasCaret, anchor, caretAnchor } = state;
  const canRefine = hasSelection && !isRefining;

  // Bubble lives in a portal so ancestor overflow / transform / contain
  // can't trap or clip a `position: fixed` child. Anchored to the point
  // where the user released the mouse to finish their drag-selection.
  const bubble =
    hasSelection && anchor
      ? createPortal(
          // Outer wrapper is `pointer-events-none` so a mousedown landing
          // anywhere in its bounding box (other than the buttons) falls
          // through to the editor — letting the user start a fresh
          // selection without first dismissing the bubble.
          <div
            role="toolbar"
            data-overlay-portal=""
            aria-label="Selection actions"
            className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full"
            style={{
              top: anchor.y - 12,
              left: anchor.x,
            }}
          >
            <div
              className="pointer-events-auto flex items-center gap-1 rounded-lg border bg-background-white p-1 shadow-lg"
              onMouseDown={(e) => {
                // Preserve the selection across the click ON the buttons.
                e.preventDefault();
              }}
            >
              {canRestore && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1 px-2 text-xs"
                  onClick={handleOriginal}
                >
                  <Undo2 className="size-3.5" />
                  Original Wording
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1 px-2 text-xs text-primary hover:bg-primary-50"
                onClick={handleRefine}
                disabled={!canRefine}
              >
                {isRefining ? (
                  <RotateCcw className="size-3.5 animate-spin" />
                ) : (
                  <Sparkles className="size-3.5" />
                )}
                Refine Wording
              </Button>
            </div>
          </div>,
          document.body
        )
      : null;

  // Floating mic at the cursor — appears when the user has just clicked
  // into the editor (caret placed, no selection). Anchored to the mouseup
  // point. MDRS-292: dictation inserts at the CARET position under the click
  // (clickedCaretPosRef), falling back to the end of the paragraph only when
  // the caret couldn't be mapped.
  const caretInsertPosRef = useRef<number | null>(null);
  // MDRS-292: in-flight interim (partial) dictation range. Azure streams
  // mid-turn hypotheses; we write each into the doc and replace it as it
  // refines, then swap in the final on end-of-turn — so text appears as the
  // user speaks instead of only landing after the end-of-turn pause. `from` is
  // the PM position the interim starts at, `size` its current PM content size,
  // `text` the last partial (for the intact check). Null `from` = no interim.
  const partialFromRef = useRef<number | null>(null);
  const partialSizeRef = useRef<number>(0);
  const partialTextRef = useRef<string>("");
  const resetPartial = () => {
    partialFromRef.current = null;
    partialSizeRef.current = 0;
    partialTextRef.current = "";
  };

  const captureCaretInsertion = () => {
    // MDRS-292: prefer the exact caret position under the click so dictated text
    // lands where the user clicked. Also move the editor's caret there so the
    // cursor is in sync when dictation starts (AC: dictation begins at the last
    // click, not the front of the line).
    const caretPos = clickedCaretPosRef.current;
    if (caretPos !== null) {
      caretInsertPosRef.current = caretPos;
      const view = getView();
      if (view) {
        try {
          view.dispatch(
            view.state.tr.setSelection(
              TextSelection.create(view.state.doc, caretPos)
            )
          );
        } catch {
          /* best-effort caret move; the insert below still targets caretPos */
        }
      }
      return;
    }
    // pm-end points just AFTER the paragraph's closing token; subtract 1
    // to land at the end of the paragraph's inline content.
    const range = clickedPmRangeRef.current;
    if (range) {
      caretInsertPosRef.current = range.to - 1;
      return;
    }
    // Fallback: the DOM data-pm-* attrs weren't readable (e.g. the user
    // clicked inside a table cell whose `.layout-paragraph` ancestor we
    // couldn't find). Resolve the paragraph from docx-editor's reported
    // selection info and walk the PM doc to find its end.
    const info = readInfo();
    const view = getView();
    if (!info?.paraId || !view) {
      caretInsertPosRef.current = null;
      return;
    }
    const paraPos = findParagraphStart(view, info.paraId);
    if (paraPos === null) {
      caretInsertPosRef.current = null;
      return;
    }
    const node = view.state.doc.nodeAt(paraPos);
    caretInsertPosRef.current = node ? paraPos + node.nodeSize - 1 : null;
  };

  // MDRS-549: the `donorMarksAt` calls below were checked for handleRefine's
  // stale-offset race and ruled out — each reads `view.state` and dispatches in
  // the SAME tick, so no position goes stale *within* a callback the way one
  // does across refine's await.
  //
  // What does persist across callbacks is `caretInsertPosRef` /
  // `partialFromRef`. The interim range is revalidated by `partialRangeIntact`
  // before reuse, but the fallback insert at the end of `onTextInsert` trusts
  // `caretInsertPosRef` unchecked, and nothing maps either ref through a
  // transaction dispatched in between (including refine's own replacement).
  // Out of scope here; see MDRS-549 for the write-up.
  const caretDictationCallback: TranscriptionCallback = {
    onFocus: () => {
      // Mic + WS just came online; gate `hide()` and outside-click cleanup
      // so the user can scroll the editor / click around without tearing
      // down the live session.
      isCaretDictationActiveRef.current = true;
      resetPartial();
      captureCaretInsertion();
      editorRef.current?.focus?.();
    },
    onEnd: () => {
      isCaretDictationActiveRef.current = false;
      resetPartial();
    },
    // MDRS-292: stream Azure's mid-turn PARTIAL hypotheses into the doc so text
    // appears as the user speaks — matching Assessment Scribe's textarea — rather
    // than only committing the final on the end-of-turn pause (the old lag). Each
    // partial replaces the running interim range; `onTextInsert` swaps in the
    // final. Empty partial = the turn is closing (final committed / no in-flight).
    onPartialUpdate: (partial: string) => {
      const view = getView();
      if (!view) return;
      if (partial === "") {
        // Turn closing with interim text still in the doc but no final to
        // replace it: leave the text, but advance the caret past it so a later
        // insert appends after it rather than in front of the leftover.
        if (partialFromRef.current !== null) {
          caretInsertPosRef.current =
            partialFromRef.current + partialSizeRef.current;
        }
        resetPartial();
        return;
      }
      try {
        const from = partialFromRef.current;
        if (
          from !== null &&
          partialRangeIntact(
            view.state.doc,
            from,
            partialSizeRef.current,
            partialTextRef.current
          )
        ) {
          // Refine the existing interim in place.
          const marks = donorMarksAt(view.state, from, true);
          const fragment = Fragment.from(
            buildInlineNodes(view.state.schema, partial, marks)
          );
          const tr = view.state.tr.replaceWith(
            from,
            from + partialSizeRef.current,
            fragment
          );
          tr.setSelection(TextSelection.create(tr.doc, from + fragment.size));
          view.dispatch(tr.scrollIntoView());
          partialSizeRef.current = fragment.size;
        } else {
          // Fresh interim range, anchored at the current caret (also covers a
          // disturbed/stale range — we abandon it and re-anchor here).
          const anchor = caretInsertPosRef.current;
          if (anchor === null) return;
          const marks = donorMarksAt(view.state, anchor, true);
          const fragment = Fragment.from(
            buildInlineNodes(view.state.schema, partial, marks)
          );
          const tr = view.state.tr.insert(anchor, fragment);
          tr.setSelection(TextSelection.create(tr.doc, anchor + fragment.size));
          view.dispatch(tr.scrollIntoView());
          partialFromRef.current = anchor;
          partialSizeRef.current = fragment.size;
        }
        partialTextRef.current = partial;
      } catch (err) {
        console.error("[overlay] partial update failed:", err);
        resetPartial();
      }
    },
    onTextInsert: (text) => {
      if (!text) return;
      const view = getView();
      if (!view) return;
      try {
        const from = partialFromRef.current;
        // Fast path: replace the intact interim range with the finalized text
        // (no double-write) — mirrors the textarea handler's partial→final swap.
        if (
          from !== null &&
          partialRangeIntact(
            view.state.doc,
            from,
            partialSizeRef.current,
            partialTextRef.current
          )
        ) {
          // MDRS-264: inherit the marks at the insertion point so dictated text
          // matches the paragraph's font instead of the schema default.
          const marks = donorMarksAt(view.state, from, true);
          const fragment = Fragment.from(
            buildInlineNodes(view.state.schema, text, marks)
          );
          const tr = view.state.tr.replaceWith(
            from,
            from + partialSizeRef.current,
            fragment
          );
          const after = from + fragment.size;
          tr.setSelection(TextSelection.create(tr.doc, after));
          view.dispatch(tr.scrollIntoView());
          caretInsertPosRef.current = after;
          resetPartial();
          return;
        }

        // No interim range (or it was disturbed) → insert at the caret.
        resetPartial();
        const pos = caretInsertPosRef.current;
        if (pos === null) return;
        const marks = donorMarksAt(view.state, pos, true);
        const fragment = Fragment.from(
          buildInlineNodes(view.state.schema, text, marks)
        );
        const tr = view.state.tr.insert(pos, fragment);
        const after = pos + fragment.size;
        // MDRS-292: move the caret to sit right after the inserted text so the
        // cursor follows dictation (and chained inserts append at the caret,
        // not the paragraph end).
        tr.setSelection(TextSelection.create(tr.doc, after));
        view.dispatch(tr.scrollIntoView());
        caretInsertPosRef.current = after;
      } catch (err) {
        console.error("[overlay] caret dictation insert failed:", err);
      }
    },
  };

  const caretMic =
    hasCaret && caretAnchor && !hasSelection
      ? createPortal(
          // Outer wrapper is `pointer-events-none` so the mic doesn't
          // block drag-selecting through its bounding box. Only the
          // button itself catches clicks — and prevents default to
          // keep editor focus through the press.
          <div
            role="presentation"
            data-overlay-portal=""
            className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full"
            style={{
              // Centered above the visible caret (MDRS-91). The anchor
              // point is the caret rect's top-left (or the paragraph
              // rect top-left when the caret API is unavailable);
              // `-translate-x-1/2` puts the mic icon's center on that
              // x, and `-translate-y-full` floats it 4px above.
              top: caretAnchor.y - 4,
              left: caretAnchor.x,
            }}
          >
            <div className="pointer-events-auto rounded-full border bg-background-white shadow-md">
              {/* No onMouseDown preventDefault here — the click on the
                  mic must reach the browser as a real user gesture so
                  AudioContext / AudioWorklet can initialise. We don't
                  need to preserve any selection on this press because
                  the PM range was captured on the original click. */}
              <Transcriber
                callback={caretDictationCallback}
                iconSize="sm"
                buttonClassName="!h-8 !w-8 rounded-full hover:bg-primary-50"
              />
            </div>
          </div>,
          document.body
        )
      : null;

  // MDRS-292 (Nat): force-stop a live caret-dictation session. Flipping the
  // active flag + clearing the anchors unmounts the mic portal, whose cleanup
  // tears down the Transcriber's WebSocket + audio graph — anything already
  // transcribed stays in the doc. Optionally move the caret to where the user
  // clicked.
  const stopCaretDictation = (moveTo: number | null) => {
    isCaretDictationActiveRef.current = false;
    caretAnchorRef.current = null;
    clickedParaIdRef.current = null;
    clickedPmRangeRef.current = null;
    clickedCaretPosRef.current = null;
    caretInsertPosRef.current = null;
    resetPartial();
    setState(INITIAL_STATE);
    if (moveTo !== null) {
      const view = getView();
      if (view) {
        try {
          view.dispatch(
            view.state.tr.setSelection(TextSelection.create(view.state.doc, moveTo))
          );
        } catch {
          /* best-effort caret move */
        }
      }
    }
  };

  return (
    <>
      {bubble}
      {caretMic}
      <ConfirmationModal
        open={stopPrompt !== null}
        onOpenChange={(open) => {
          // Dismiss (Esc / overlay click) = the safe default: keep recording.
          if (!open) setStopPrompt(null);
        }}
        type="warning"
        icon={<Mic className="size-10 text-destructive" strokeWidth={2} />}
        title="Recording in progress"
        description="Moving the cursor will stop the current dictation. Anything already transcribed stays in place — stop recording and move the cursor here, or keep recording where you left off."
        confirmButtonText="Stop recording"
        cancelButtonText="Keep recording"
        onConfirm={() => {
          const pos = stopPrompt?.caretPos ?? null;
          setStopPrompt(null);
          stopCaretDictation(pos);
        }}
        onCancel={() => setStopPrompt(null)}
      />
    </>
  );
};
